import '../styles/SelectQuestion.scss';
import * as React from 'react';
import bind from 'bind-decorator';
import { filter, flatMap, keys } from 'lodash-es';
import { FormInterface } from 'Interfaces/Forms/FormsInterface';
import { QuestionInterface, questionTypes } from 'Interfaces/Forms/QuestionInterface';
import { ConfirmationModal } from 'views/Modals/ConfirmationModal';
import { LooseObject } from '../../../Interfaces/LooseObject';
import { DataPoint } from '../../../Interfaces/DataPoint';
import { ClientPersistInterface } from '../../../Interfaces/ClientPersistInterface';
import FormUtils from '../utils/FormUtils';
import { getLocalization, globalWindow } from '../../../global/global';
import { getMediaView, renderQuestions } from '../utils/qnrenderer';
import { pad } from '../../../utils/utils';
import { checkValidationRuleError, getSubQuestions, haveValue, resetSubQuestionValues } from '../utils/utils';
import { Elements } from './index';
import QuestionLabel from './QuestionLabel';
import SelectQuestionOption from './extras/SelectQuestionOption';

export interface Props {
  dataPoint: DataPoint;
  question: QuestionInterface;
  updateAnswer: (value: LooseObject) => void;
  formUtils: FormUtils;
  isTable: boolean;
  forms: LooseObject[];
  newAnswer: boolean;
  clientPersist: ClientPersistInterface;
  edit: boolean;
  parentModel?: FormInterface;
  parentDataPoint?: DataPoint;
  isSubQuestion?: boolean;
  children?: JSX.Element | JSX.Element[];
}

interface InputValue {
  checked: boolean;
  value: string;
  type: string;
}

interface State {
  value: string[];
  edit: boolean;
  options: LooseObject[];
  inputType: string;
  likertClass: string;
  isTable: boolean;
  version: number;
  showApprove: boolean;
  namePrefix: string; // option name. has to be unique per question. This is here because of tables.
  obj: LooseObject;
  hasError: boolean;
  errorText?: string;
  selectedValue?: InputValue;
}

/*
  This Class implements Select questions i.e Single, Multiple, Status, Liker, Digital Signature & Lookup value Questions
*/
export default class SelectQuestion extends React.Component <Props, State> {

  constructor(props) {
    super(props);
    const { dataPoint, question, formUtils } = this.props;
    let options: LooseObject[] = [];
    const prefix = Math.floor(Math.random() * 10000);
    const namePrefix = `${question.id}-${prefix}`;
    /*
      Set the options to the state.
    */
    if (
      question.type === questionTypes.BOOLEAN_QUESTION || question.type === questionTypes.DIGITAL_SIGNATURE_QUESTION
    ) {
      const listItem = [{value: getLocalization('yes'), id : 'Yes'}, {value: getLocalization('no'), id: 'No'}];
      options = listItem;
    } else if (question.type === questionTypes.LOOKUP_VALUES_QUESTION) {
      const parts = question.lookupValue?.split(';') || [];
      const listItem: LooseObject[] = [];
      let i = 0;
      for (let part of parts) {
        part = parts[i].split(',');
        listItem.push({value: part[0], lookupvalues: parts[i], headers: parts[0]});
        i++;
      }
      options = listItem;
    } else {
      options = question.listItems?.listItem || [];
    }
    const obj = {};
    if (question.script && question.script !== '') {
      const qnId = formUtils.getQuestionId(question.script);
      if (qnId) {
        obj[qnId] = dataPoint[question.id];
      }
    }
    this.state = {
      value: dataPoint[question.id] || dataPoint[question.id] === false ? this.getValue(dataPoint[question.id]) :
        [],
      edit: props.edit,
      options: options,
      inputType: question.type === questionTypes.SELECT_MULTIPLE_QUESTION ? 'checkbox' : 'radio',
      // Likert question options should be inline.
      likertClass: question.type ===
        questionTypes.LIKERT_SCALE_QUESTION || question.styleBoolean || question.showOptionsHorizontally
        ? 'd-inline me-2 ms-2' : '',
      isTable: props.isTable,
      version: formUtils.getSchemaVersion(),
      showApprove: this.getShowApprove(),
      namePrefix: namePrefix,
      obj: obj,
      hasError: false
    };
  }

  @bind
  private getValue(value: string | boolean): string[] {
    if (value === true || value === false) {
      return value === true ? ['Yes'] : ['No'];
    }
    return value.split(',').filter( v => v.trim() !== '' );
  }

  /*
    Gets the value of showApprove if it is true or false.
  */
  @bind
  private getShowApprove(): boolean {
    const { dataPoint, question } = this.props;
    if (question.id !== 'taskstatus') {
      return false;
    }
    const { toapprove } = dataPoint;
    if (toapprove) {
      const approvers = filter(toapprove.split(','), (id) => {
        return parseInt(id, 10) === globalWindow.userID;
      });
      if (approvers.length > 0) {
        return true;
      }
    }
    return false;
  }

  @bind
  private getOptionText(value: string): string | undefined {
    const { options } = this.state;
    const opt = options.find(f => f.id === value || f.id === value);
    if (opt) {
      return opt.value;
    }
    return undefined;
  }

  @bind
  private doValueChange(inputValue: InputValue | null) {
    const { question } = this.props;
    const { inputType } = this.state;
    let value: string[] = Object.assign([], this.state.value);
    let newDataModel = {};
    let option;
    if (inputType === 'radio' || !inputValue) {
      if (!inputValue || (!inputValue.checked && inputValue.type !== 'select-one')) {
        value = [];
      } else if (inputValue.checked || inputValue.type === 'select-one') {
        value = [inputValue.value];
      }
      if (this.state.value && this.state.value.length > 0) {
        option = this.getOptionText(this.state.value[0]);
      }
      // can't unselect all if there is default value
      if (value.length === 0 && question.default) {
        value = [question.default];
      }
    } else {
      if (inputValue.checked) {
        value.push(inputValue.value);
      } else {
        value = value.filter(v => v !== inputValue.value);
        option = this.getOptionText(inputValue.value);
      }
    }
    if (option) {
      newDataModel = resetSubQuestionValues(question, option);
    }
    if (question.id === 'scheduleStatus' && value[0] === 'Unscheduled') {
      newDataModel['scheduleDate'] = '';
    }
    this.setState({ value, selectedValue: undefined });
    this.updateAnswer(value.join(','), newDataModel);
  }

  @bind
  private handleChange(e) {
    const { question, clientPersist, dataPoint } = this.props;
    const { inputType } = this.state;
    const value: string[] = Object.assign([], this.state.value);

    let hasSubQuestions = false;
    if ((inputType === 'radio' || e.target.type === 'select-one') && value.length > 0) {
      const text = this.getOptionText(this.state.value[0]);
      if (text) {
        const subQuestions = getSubQuestions(question, text, clientPersist);
        hasSubQuestions = subQuestions.length > 0 && haveValue(subQuestions, dataPoint);
      }
    } else {
      if (!e.target.checked) {
        const text = this.getOptionText(e.target.value);
        if (text) {
          const subQuestions = getSubQuestions(question, text, clientPersist);
          hasSubQuestions = subQuestions.length > 0 && haveValue(subQuestions, dataPoint);
        }
      }
    }
    if (hasSubQuestions) {
      if (!e) {
        this.setState({ selectedValue: { checked: false, value: value[0], type: '' }});
      } else {
        this.setState({ selectedValue: { checked: e.target.checked, value: e.target.value, type: e.target.type }});
      }
      return;
    }
    this.doValueChange({
      checked: e ? e.target.checked : false, value: e ? e.target.value : '', type: e ? e.target.type : 'select-one'
    });
  }

  /*
    If this is taskstatus question, we set values appropriately based on the following rules.
  */
  private getTaskValues(status: string): LooseObject {
    const value = {} as LooseObject;
    const today = new Date();

    if (status === 'assigned') {
      value['assigndate'] = `${today.getFullYear()}${'-'}${pad(today.getMonth() + 1)}${'-'}${pad(today.getDate())}`;
      value['donedate'] = '';
      value['doneby'] = '';
    } else if (status === 'approved' || status === 'approvedwithcomments') {
      value['approveddate'] = `${today.getFullYear()}${'-'}${pad(today.getMonth() + 1)}${'-'}${pad(today.getDate())}`;
    } else if (status === 'done') {
      value['donedate'] = `${today.getFullYear()}${'-'}${pad(today.getMonth() + 1)}${'-'}${pad(today.getDate())}`;
    } else if (status === 'inprogress') {
      value['startdate'] = `${today.getFullYear()}${'-'}${pad(today.getMonth() + 1)}${'-'}${pad(today.getDate())}`;
    }
    return value;
  }

  /**
   * @param value
   * @param subQuestionValues
   */
  @bind
  private updateAnswer(value, subQuestionValues?: LooseObject) {
    const { updateAnswer, question } = this.props;
    let newAns = {};
    if (question.type === questionTypes.BOOLEAN_QUESTION) {
      if (value === 'Yes') {
        value = true;
      } else if (value === 'No') {
        value = false;
      }
      newAns[question.id] = value;
    } else {
      newAns[question.id] = value;
    }
    if (question.id === 'taskstatus') {
      const newValue = this.getTaskValues(value);
      newAns = Object.assign({}, newAns, newValue);
    }
    if (value) {
      if (question.id === 'personStatus') {
        newAns = {...newAns, [`${value}ts`]: Date.now()};
      } else if (question.id === 'stage') {
        newAns = {...newAns, [`${value}ts`]: Date.now()};
      } else if (question.id === 'companyStatus') {
        newAns = {...newAns, [`${value}ts`]: Date.now()};
      }
    }
    if (subQuestionValues) {
      newAns = Object.assign({}, newAns, subQuestionValues);
    }
    updateAnswer(newAns);
  }

  @bind
  private getSubQuestions(option: string, value: string): JSX.Element[] | null {
    const {
      question, dataPoint, updateAnswer, formUtils, forms, newAnswer, parentModel, parentDataPoint, clientPersist, edit
    } = this.props;
    const v = this.state.value;
    if (question.triggerValues && question.triggerValues.triggerValue) {
      for (const triggerValue of question.triggerValues.triggerValue) {
        if (triggerValue.value === option) {
          if (question.type === questionTypes.DIGITAL_SIGNATURE_QUESTION) {
            /* const questions = !newAnswer ? filter(triggerValue.action.subQuestions.question, (qn) =>
              qn.type !== questionTypes.BOOLEAN_QUESTION
            ) : triggerValue.action.subQuestions.question;*/
            const questions = triggerValue.action.subQuestions.question;
            return renderQuestions(questions, dataPoint,
              false, forms, updateAnswer, formUtils, newAnswer,
              clientPersist, undefined, undefined, undefined, true, !edit
            );
          }
          const sqs = renderQuestions(triggerValue.action.subQuestions.question, dataPoint, false, forms, updateAnswer,
            formUtils, newAnswer, clientPersist, parentModel, parentDataPoint, undefined, true,
            v.indexOf(value) === -1 || !edit
          );
          return sqs;
        } /* reset in data model the questions that are not visible. */
      }
    }
    return null;
  }

  /*
    Checks whether a task option should be visible.
  */
  @bind
  private isTaskOptionVisible(option: string): boolean {
    const { showApprove, value } = this.state;
    if (!showApprove && option === 'approved' || option === 'approvedwithcomments') {
      return false;
    }
    if (
      (option === 'inprogress' || option === 'removed') &&
      ['assigned', 'removed', 'inprogress'].indexOf(value[0]) === -1
    ) {
      return false;
    }
    return true;
  }

  /*
    This function returns the options to be rendered as radio/check boxes.
  */
  @bind
  public getQuestionOptions(): JSX.Element {
    const { value, options, version, inputType, likertClass, namePrefix, isTable, hasError  } = this.state;
    const errorClass = hasError ? 'is-invalid' : '';
    const { question, formUtils } = this.props;
    const subQuestions: JSX.Element[] = [];
    const optionEl: any[] = options.map( opt => {
      if (question.id === 'taskstatus' && !this.isTaskOptionVisible(opt.id)) {
        return null;
      }
      const v = version >= 2 ? opt.id : opt.value;
      const optValue = question.type === questionTypes.BOOLEAN_QUESTION ||
      question.type === questionTypes.DIGITAL_SIGNATURE_QUESTION ? opt.id : opt.value;
      const checked = value.indexOf(opt.id) !== -1 || value.indexOf(opt.value) !== -1;
      if (checked && (question.styleBoolean || question.showOptionsHorizontally)) {
        const sq = this.getSubQuestions(optValue, v);
        if (sq) {
          sq.forEach(s => {
            subQuestions.push(s);
          });
        }
      }
      return (
        <SelectQuestionOption
          key={`${question.id}_${v}_${checked}`}
          option={opt}
          getSubQuestions={this.getSubQuestions}
          checked={checked}
          handleChange={this.handleChange}
          question={question}
          version={version}
          isTable={isTable}
          inputType={inputType}
          likertClass={likertClass}
          namePrefix={namePrefix}
          edit={this.props.edit}
          errorClass={errorClass}
          form={formUtils.getModel()}
        />
      );
    });
    return (
      <>
        {optionEl}
        {subQuestions.length > 0 ? (
          <div className="container-fluid subquestions" key={`SubQuestion_${question.id}`}>
            {subQuestions}
          </div>
        ) : null}
      </>
    );
  }

  /*
    This function returns a dropdown for the options.
    When we have single select with 10 or more options or 3 or more options for tables this is rendered
  */
  @bind
  public getDropDownSelect(): JSX.Element {
    const { value, options, version, hasError } = this.state;
    const { question, clientPersist } = this.props;
    // US-3524
    const enumDisableActionStatusQn = question.id === 'actionStatus' && clientPersist.roles.includes('enumerator');
    const disabledOptions = ['agreed', 'confirmed', 'cancelled'];
    let option;
    const hasErrorClass = hasError ? 'is-invalid' : '';
    const optionsEl = [
      (<option value="" key={`${question.id}_selectone}`}>Select one</option>)
    ].concat(options.map( opt => {
      const v = version >= 2 ? opt.id : opt.value;
      if (value.indexOf(opt.id) !== -1) {
        option = opt.value;
      }
      return (
        <option
          value={v}
          key={`${question.id}_${v}`}
          disabled={enumDisableActionStatusQn && disabledOptions.indexOf(v.toLowerCase()) !== -1}
        >
          {opt.value}
        </option>
      );
    }));
    return (
      <React.Fragment>
        <select
          onChange={this.handleChange}
          name={question.id}
          className={`form-control ${hasErrorClass}`}
          disabled={!this.props.edit}
          value={value.length > 0 ? value[0] : undefined}
          data-testid={'select-question-dropdown'}
        >
          {optionsEl}
        </select>
        {option && value.length > 0 && this.getSubQuestions(option, value[0])}
      </React.Fragment>
    );
  }

  public static getDerivedStateFromProps(props: Props, state: State) {
    const { dataPoint, question, formUtils } = props;
    const { obj, value } = state;

    let newState: any = null;
    if (question.script && question.script !== '') {
      newState = filterOptions(dataPoint, question, formUtils, obj, value);
    }

    if (
      question.type === questionTypes.BOOLEAN_QUESTION || question.type === questionTypes.DIGITAL_SIGNATURE_QUESTION
    ) {
      if (dataPoint[question.id] === true || dataPoint[question.id] === false) {
        const value = dataPoint[question.id] ? 'Yes' : 'No';
        if (value !== state.value.join(',')) {
          newState = newState || {};
          newState.value =  [value];
        }
      }
    } else if (dataPoint[question.id] && dataPoint[question.id] !== state.value.join(',')) {
      newState = newState || {};
      newState.value =  dataPoint[question.id].split(',').filter( v => v.trim() !== '' );
    }
    if (dataPoint['validate']) {
      let hasError = false;
      let errorText: string | undefined;
      if (!question.optional && state.value.length === 0) {
        hasError = true;
      } else {
        if (dataPoint['invalidValidationRules']) {
          const ivr = dataPoint['invalidValidationRules'];
          const errors = checkValidationRuleError(question.id, ivr, formUtils);
          hasError = errors.hasError;
          errorText = errors.errorText;
        }
      }
      newState = newState || {};
      newState.hasError = hasError;
      newState.errorText = errorText;
    }
    return newState;
  }

  public shouldComponentUpdate() {
    /*    return this.state.value !== nextState.value || this.state.edit !== nextState.edit
      || nextProps.dataPoint.validate !== this.props.dataPoint.validate;*/
    return true;
  }

  /*
    renders include file.
  */
  @bind
  private renderIncludeFile(): JSX.Element[] | undefined {
    const { question, dataPoint, formUtils, updateAnswer } = this.props;
    if (question.includeFile || formUtils.getModel().childUI) {
      return [(
        <Elements.FileQuestion
          key={`include-file-select-question-${question.id}-${dataPoint.id}`}
          accept={question.accept}
          question={question}
          dataPoint={dataPoint}
          updateAnswer={updateAnswer}
          edit={formUtils.getModel().childUI ? false : this.props.edit}
        />
      )];
      /* const types = question.accept.split(',');
      const fileInputs = types.map((type, index) => {
        return (
          <Elements.FileQuestion
            key={`${question.id}-file-${index}`}
            accept={type}
            question={question}
            dataPoint={dataPoint}
            updateAnswer={updateAnswer}
            edit={formUtils.getModel().childUI ? false : this.props.edit}
          />
        );
      });
      return fileInputs;*/
    }
    return undefined;
  }

  public render(): JSX.Element {
    const { options, isTable, inputType, hasError, errorText, selectedValue } = this.state;
    const { question, formUtils, dataPoint, isSubQuestion, parentModel, parentDataPoint } = this.props;
    const required = question.optional ? null : (<span className="text-danger">{` * `}</span>);
    const hasErrorClass = hasError ? 'is-invalid' : '';

    const confirmChange = selectedValue ? (
      <ConfirmationModal
        onConfirm={() => this.doValueChange(selectedValue)}
        onCancel={() => this.setState({ selectedValue: undefined })}
        onClose={() => this.setState({ selectedValue: undefined })}
        localizations={{
          confirm: getLocalization('ok'),
          cancel: getLocalization('cancel'),
          body: (<p>{getLocalization('confirmOptionChange')}</p>),
          header: (<>{getLocalization('confirm')}</>),
          confirmStyle: 'primary'
        }}
      />
    ) : null;
    return (
      <div
        className={`${'form-group'} ${hasErrorClass}  ${formUtils.getResponsiveView(question, isSubQuestion)}`}
        data-testid={'select-question-div'}
      >
        {confirmChange}
        {getMediaView(question)}
        <QuestionLabel
          question={question}
          dataPoint={dataPoint}
          formUtils={formUtils}
          parentModel={parentModel}
          parentDataPoint={parentDataPoint}
        >
          {required}
        </QuestionLabel>
        {inputType === 'radio' && (options.length > 9 || (options.length > 3 && isTable)) ?
          this.getDropDownSelect() : this.getQuestionOptions()}
        {this.renderIncludeFile()}
        {hasError && errorText && (
          <div className="invalid-feedback">{errorText}</div>
        )}
        {this.props.children}
      </div>
    );
  }
}

export const filterOptions  = (dataPoint: DataPoint, question, formUtils: FormUtils, obj, currentValue) => {
  const options = [...question.listItems.listItem];
  let newState: any = null;
  const  key = keys(obj);
  for (const k of key) {
    if (dataPoint[k] !== obj[k]) {
      const newValues = dataPoint[k];
      if (newValues) {
        const qn = formUtils.getQuestion(k);
        const ids = newValues.split(',').map( v => v.trim());
        const selectedOptions = qn.listItems && qn.listItems.listItem.filter((opt) => {
          return ids.indexOf(opt.id) !== -1; //  || ids.indexOf(opt.value) !== -1;
        });
        const optionCodes = flatMap(selectedOptions, (opt) => {
          return opt.code;
        });
        if (optionCodes.length > 0) {
          newState = newState || {};
          newState.options = options.splice(options.length).concat(filter(question.listItems.listItem, (opt) => {
            return optionCodes.indexOf(opt.code) !== -1;
          }));
          const values = flatMap(newState.options, (opt) => {
            return opt.id;
          });
          newState.value = values.filter(v => {
            return currentValue.indexOf(v) !== -1;
          });
        }
      }
    }
  }
  return newState;
};
